import { useEffect, useRef, useState } from "react";

import { twColorToHex } from "@/utils/tailwindColors";

import { graphManager } from "./GraphManager";
import { useAppletThemeColors } from "./useAppletThemeColors";

import type { GGBApi, GraphOptions } from "./types";
import type { RefObject } from "react";

export function useGeogebraApplet(
  containerRef: RefObject<HTMLElement | null>,
  enabled = true,
  options: Partial<GraphOptions> = {},
  theme: "dark" | "light",
) {
  const [api, setApi] = useState<null | GGBApi>(null);
  const themeColors = useAppletThemeColors(options, theme);
  const optionsRef = useRef({
    ...themeColors,
    ...options,
  });

  const active = enabled && graphManager.ggbReady;

  useEffect(() => {
    if (enabled) graphManager.init();
  }, [enabled]);

  useEffect(() => {
    api?.setGraphicsOptions(1, {
      gridColor: twColorToHex(themeColors.gridColor),
      bgColor: twColorToHex(themeColors.bgColor),
      axesColor: twColorToHex(themeColors.axesColor),
    });
  }, [api, themeColors]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !active) return;

    let aplt: GGBApi | null;

    const abort = new AbortController();
    async function run() {
      if (abort.signal.aborted || !container) {
        return;
      }
      aplt = await graphManager.createApplet({
        options: optionsRef.current,
        abort: abort.signal,
        container,
      });
      if (!abort.signal.aborted) {
        setApi(aplt);
      }
    }

    // Geogebra doesn't want to render the applet immediately for some reason
    queueMicrotask(run);

    return () => {
      if (aplt) aplt.remove();
      setApi(null);
      abort.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return { api };
}
